import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

import { getBlogImageUrl } from "../lib/blog.utils";
import type { BlogDetail, BlogListItem } from "../types/blog.types";

type BlogListItemProps = {
  post: BlogListItem | BlogDetail;
  featured?: boolean;
  showImage?: boolean;
  className?: string;
};

export function BlogListItem({
  post,
  featured = false,
  showImage = true,
  className,
}: BlogListItemProps) {
  const imageUrl = getBlogImageUrl(post.featured_image_file);

  return (
    <article
      className={cn(
        "group py-8 md:py-10",
        featured && "first:pt-0",
        className,
      )}
    >
      <div
        className={cn(
          "grid gap-5 md:gap-8",
          showImage
            ? featured
              ? "md:grid-cols-[9rem_minmax(0,1fr)_15rem]"
              : "md:grid-cols-[9rem_minmax(0,1fr)_12rem]"
            : "md:grid-cols-[9rem_minmax(0,1fr)]",
        )}
      >
        <div className="space-y-2 pt-1 text-sm text-muted-foreground">
          <p>{formatDate(post.published_at)}</p>
          {post.category_name ? (
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {post.category_name}
            </p>
          ) : null}
        </div>

        <div className="min-w-0">
          <Link href={`/blog/${post.slug}`} className="block">
            <h2
              className={cn(
                "max-w-4xl text-balance font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary",
                featured ? "text-3xl md:text-5xl" : "text-2xl md:text-[2rem]",
              )}
            >
              {post.title}
            </h2>
            <p
              className={cn(
                "mt-3 max-w-3xl text-base leading-8 text-muted-foreground",
                featured ? "line-clamp-4" : "line-clamp-3",
              )}
            >
              {post.excerpt}
            </p>
          </Link>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span>{post.author_name || "Booking Expert Editorial"}</span>
            <span className="hidden h-1 w-1 rounded-full bg-slate-300 md:block dark:bg-slate-700" />
            <Link
              href={`/blog/${post.slug}`}
              className="inline-flex items-center gap-1.5 font-medium text-foreground transition-colors group-hover:text-primary"
            >
              Read more
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {showImage ? (
          <Link
            href={`/blog/${post.slug}`}
            className="relative hidden overflow-hidden rounded-2xl border border-slate-200/70 bg-slate-100 md:block dark:border-slate-800/70 dark:bg-slate-900"
          >
            <div className="relative aspect-[4/3] h-full min-h-[140px]">
              <Image
                src={imageUrl}
                alt={post.featured_image_alt_text || post.title}
                fill
                sizes={
                  featured
                    ? "(min-width: 1280px) 240px, 30vw"
                    : "(min-width: 1280px) 200px, 30vw"
                }
                className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </Link>
        ) : null}
      </div>
    </article>
  );
}
